/**
 * LE RATTACHEMENT DES CLIENTS À PENNYLANE — la partie qui CALCULE.
 *
 * Maquette `docs/maquettes/rattachement-clients-pennylane.html`, validée le
 * 11/09/2026. Décision du même jour : « stafa ne numerote plus il utilise
 * pennylane ». Une facture Pennylane désigne son client par un identifiant
 * chez eux ; sans rattachement, Stafa ne peut pas facturer.
 *
 * Hors de React, comme `cherchable.ts` et `facturation.ts` : ce fichier
 * s'éprouve sans navigateur, et c'est ce qui permet de vérifier les trois
 * états plutôt que de les regarder.
 */

import type { Choix } from './cherchable.js';

/** Un client Stafa, tel que l'API le rend à cet écran. */
export interface ClientARattacher {
  readonly id: string;
  readonly code: string | null;
  readonly nom: string;
  /** Saisi à part, ou déduit des neuf premiers chiffres du SIRET. */
  readonly siren: string | null;
  readonly actif: boolean;
  /**
   * EN CHAÎNE, jamais en nombre. La colonne est un `bigint`, et `JSON.parse`
   * arrondirait sans rien dire au-delà de 2^53. Une chaîne ne s'arrondit pas.
   */
  readonly pennylane_client_id: string | null;
}

/** Un client Pennylane, tel que l'API le rend à cet écran. */
export interface ClientPennylane {
  readonly id: number;
  readonly nom: string;
  readonly siren: string | null;
  readonly ville: string | null;
  readonly code_postal: string | null;
}

/**
 * LES TROIS ÉTATS D'UNE LIGNE, et il n'y en a pas un quatrième :
 *
 *   `rattache` — l'identifiant est posé ET le client existe chez Pennylane ;
 *   `a_faire`  — aucun identifiant ;
 *   `perdu`    — un identifiant est posé mais AUCUN client Pennylane ne le
 *                porte. Le client a été supprimé chez eux, ou le rattachement
 *                a été fait vers un autre dossier.
 *
 * `perdu` MÉRITE SON ÉTAT et ne se confond pas avec `a_faire` : dans un cas il
 * n'y a rien eu, dans l'autre quelque chose a disparu. Les traiter pareil
 * ferait réparer sans jamais comprendre — et le même client disparaîtrait à
 * nouveau la semaine suivante.
 */
export type EtatRattachement = 'rattache' | 'a_faire' | 'perdu';

/** Une ligne du tableau, prête à dessiner. */
export interface LigneRattachement {
  readonly client: ClientARattacher;
  readonly etat: EtatRattachement;
  /** Le client Pennylane rattaché, s'il a été retrouvé. */
  readonly chezPennylane: ClientPennylane | null;
  /**
   * Les SIREN des deux côtés concordent-ils ?
   *
   * `null` quand l'un des deux manque — et c'est un troisième cas, pas un
   * « non » : on ne peut pas dire que deux choses diffèrent quand on n'en
   * connaît qu'une. L'écran affiche alors qu'il ne sait pas, au lieu d'alerter
   * à tort sur la moitié des lignes.
   */
  readonly sirenConcorde: boolean | null;
}

/**
 * Range les clients Pennylane par identifiant, en CHAÎNE.
 *
 * La clé est une chaîne des deux côtés : celle de Stafa vient d'un `bigint`
 * rendu en texte, celle de Pennylane d'un nombre JSON. Comparer un nombre à
 * une chaîne avec `===` rend toujours faux, et toutes les lignes passeraient
 * en « perdu » — une panne totale, sans message d'erreur.
 */
export function indexerPennylane(
  clients: readonly ClientPennylane[],
): ReadonlyMap<string, ClientPennylane> {
  const index = new Map<string, ClientPennylane>();
  for (const c of clients) {
    index.set(String(c.id), c);
  }
  return index;
}

/** Les neuf premiers chiffres, ou `null`. Un SIRET vaut son SIREN. */
function neufChiffres(valeur: string | null): string | null {
  if (valeur === null) return null;
  const chiffres = valeur.replace(/\D/gu, '');
  return chiffres.length >= 9 ? chiffres.slice(0, 9) : null;
}

/** L'état d'un client, et ce qu'on sait de son homologue. */
export function ligneDe(
  client: ClientARattacher,
  index: ReadonlyMap<string, ClientPennylane>,
): LigneRattachement {
  if (client.pennylane_client_id === null) {
    return { client, etat: 'a_faire', chezPennylane: null, sirenConcorde: null };
  }

  const chezPennylane = index.get(client.pennylane_client_id) ?? null;
  if (chezPennylane === null) {
    return { client, etat: 'perdu', chezPennylane: null, sirenConcorde: null };
  }

  const ici = neufChiffres(client.siren);
  const la = neufChiffres(chezPennylane.siren);
  return {
    client,
    etat: 'rattache',
    chezPennylane,
    sirenConcorde: ici === null || la === null ? null : ici === la,
  };
}

/**
 * Les lignes à dessiner.
 *
 * LES INACTIFS SONT MASQUÉS PAR DÉFAUT : on ne facture pas un client inactif,
 * et l'afficher allongerait la liste de lignes dont aucune n'appelle de
 * travail. Le filtre est ICI et non dans l'API — la case doit répondre sans
 * recharger.
 *
 * L'ORDRE VIENT DE L'API, qui range déjà par nom. On ne remet pas les
 * « à faire » en tête : la liste changerait d'ordre à chaque rattachement, et
 * on perdrait où l'on en est.
 */
export function lignesRattachement(
  clients: readonly ClientARattacher[],
  index: ReadonlyMap<string, ClientPennylane>,
  avecInactifs: boolean,
): readonly LigneRattachement[] {
  return clients
    .filter((c) => avecInactifs || c.actif)
    .map((c) => ligneDe(c, index));
}

/** Ce qu'annonce le bandeau du haut. */
export interface Avancement {
  readonly rattaches: number;
  readonly total: number;
  /** Ceux qu'on ne pourrait pas facturer aujourd'hui : à faire ET perdus. */
  readonly bloques: number;
  /** De 0 à 100, pour la jauge. Vaut 100 quand il n'y a aucun client. */
  readonly pourcentage: number;
}

/**
 * L'AVANCEMENT NE COMPTE QUE LES CLIENTS ACTIFS, quoi qu'affiche la case des
 * inactifs. Sinon, cocher « afficher les inactifs » ferait chuter le compteur
 * sans que rien n'ait changé — et on croirait à une perte.
 */
export function avancementRattachement(
  clients: readonly ClientARattacher[],
  index: ReadonlyMap<string, ClientPennylane>,
): Avancement {
  const actifs = clients.filter((c) => c.actif);
  const rattaches = actifs.filter((c) => ligneDe(c, index).etat === 'rattache').length;
  const total = actifs.length;
  return {
    rattaches,
    total,
    bloques: total - rattaches,
    // ZÉRO CLIENT VAUT CENT POUR CENT, et non une division par zéro : il n'y a
    // rien à faire, donc rien ne reste à faire.
    pourcentage: total === 0 ? 100 : Math.round((rattaches / total) * 100),
  };
}

/**
 * Les choix proposés dans le champ de recherche.
 *
 * LE LIBELLÉ EST LE NOM SEUL, parce que c'est aussi ce qui s'affichera DANS LE
 * CHAMP une fois le choix fait, dans une colonne de tableau. Le SIREN et la
 * ville vont dans `detail`, sur une seconde ligne — et la recherche porte sur
 * les deux, donc taper neuf chiffres suffit à désigner un client sans
 * ambiguïté.
 *
 * L'IDENTIFIANT EST MIS EN CHAÎNE ici, une fois, pour tout le reste de
 * l'écran : `ChoixCherchable` ne manipule que des chaînes.
 */
export function choixPennylane(clients: readonly ClientPennylane[]): readonly Choix[] {
  return clients.map((c) => {
    const morceaux: string[] = [];
    if (c.siren !== null) morceaux.push(c.siren);
    if (c.code_postal !== null) morceaux.push(c.code_postal);
    if (c.ville !== null) morceaux.push(c.ville);

    const choix: { id: string; libelle: string; detail?: string } = {
      id: String(c.id),
      libelle: c.nom,
    };
    // Pas de `detail` vide : une seconde ligne blanche sous chaque nom
    // agrandirait la liste sans rien apprendre.
    if (morceaux.length > 0) {
      choix.detail = morceaux.join(' · ');
    }
    return choix;
  });
}
